import { ArrowRight, Layers3 } from "lucide-react";
import { Link } from "react-router-dom";

type ButtonVariant = {
  number: string;
  name: string;
  description: string;
  background: string;
  foreground: string;
  muted: string;
};

const variants: readonly ButtonVariant[] = [
  {
    number: "01",
    name: "Midnight Lime",
    description: "The strongest match for the current premium dark direction.",
    background: "#c9f76f",
    foreground: "#10151f",
    muted: "#314a2b",
  },
  {
    number: "02",
    name: "School Teal",
    description: "A dependable brand-led option with excellent contrast.",
    background: "#156064",
    foreground: "#f6f8f0",
    muted: "#b7ded9",
  },
  {
    number: "03",
    name: "Ledger Gold",
    description: "Warm, confident and connected to fees and accounting.",
    background: "#f6c32f",
    foreground: "#10151f",
    muted: "#7d5d00",
  },
  {
    number: "04",
    name: "Signal Coral",
    description: "A lively accent for action-led screens and onboarding.",
    background: "#ff8b6a",
    foreground: "#10151f",
    muted: "#7d3022",
  },
];

const moduleLabels = ["Students", "Fees", "Timetable", "Messages", "Reports", "Settings"];

export function ButtonVariantsPage() {
  return (
    <div className="min-h-screen bg-[#10151f] text-[#f6f8f0]">
      <header className="border-b border-white/10 bg-[#0b1018]">
        <div className="mx-auto flex max-w-[1440px] items-center justify-between gap-4 px-5 py-4 lg:px-8">
          <Link to="/" className="font-display text-xl font-extrabold tracking-[-0.03em]">
            Waka<span className="text-[#f6c32f]">skul</span>
          </Link>
          <Link to="/" className="rounded border border-white/20 px-3 py-2 text-sm text-white/75 hover:bg-white/10 hover:text-white">
            Back to site
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-[1440px] px-5 py-10 lg:px-8 lg:py-14">
        <div className="max-w-3xl">
          <p className="font-mono text-[0.65rem] font-bold uppercase tracking-[0.2em] text-[#c9f76f]">WakaSkul / button study</p>
          <h1 className="mt-4 font-display text-4xl font-bold tracking-[-0.05em] sm:text-6xl">Filled buttons, four ways.</h1>
          <p className="mt-4 text-base leading-relaxed text-[#b3bec8] sm:text-lg">
            The same module menu with four solid-color directions. Choose one and I will apply it across the landing page and system.
          </p>
        </div>

        <div className="mt-10 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {variants.map((variant) => (
            <article key={variant.name} className="flex min-h-[430px] flex-col rounded border border-white/10 bg-[#171e2b] p-5 shadow-[0_18px_45px_rgba(0,0,0,0.18)]">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="font-mono text-xs font-bold text-white/35">{variant.number}</p>
                  <h2 className="mt-3 font-display text-xl font-bold">{variant.name}</h2>
                </div>
                <span className="mt-1 h-5 w-5 rounded-full" style={{ backgroundColor: variant.background }} aria-hidden="true" />
              </div>
              <p className="mt-2 min-h-12 text-sm leading-relaxed text-[#b3bec8]">{variant.description}</p>

              <div className="mt-6 grid gap-2">
                {moduleLabels.map((label) => (
                  <button
                    key={label}
                    type="button"
                    className="flex items-center justify-between gap-3 rounded px-3.5 py-3 text-left text-sm font-semibold shadow-[0_6px_14px_rgba(0,0,0,0.12)] transition hover:-translate-y-0.5"
                    style={{ backgroundColor: variant.background, color: variant.foreground }}
                  >
                    <span className="flex items-center gap-2.5"><Layers3 className="h-4 w-4" aria-hidden="true" />{label}</span>
                    <ArrowRight className="h-4 w-4" aria-hidden="true" />
                  </button>
                ))}
              </div>

              <p className="mt-auto border-t border-white/10 pt-5 text-xs font-medium" style={{ color: variant.muted }}>
                Solid fill · 4px radius · high-contrast label
              </p>
            </article>
          ))}
        </div>
      </main>
    </div>
  );
}
